import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { WhatsNewItem, WhatsNewType } from "../services/whats-new.service";
import { useWhatsNewContext } from "../hooks/WhatsNewContext";
import { useI18n } from "../i18n";

const TYPE_ICON: Record<WhatsNewType, string> = {
  release: "fa-solid fa-rocket",
  "product-sheet": "fa-solid fa-file-invoice",
  document: "fa-solid fa-file-lines",
  "brand-guide": "fa-solid fa-palette",
  graphics: "fa-solid fa-image",
  webinar: "fa-solid fa-video",
  academy: "fa-solid fa-graduation-cap",
};

// Catalog keys; resolved with t() at render.
const TYPE_LABEL: Record<WhatsNewType, string> = {
  release: "notifications.typeLabels.release",
  "product-sheet": "notifications.types.productSheet",
  document: "notifications.typeLabels.document",
  "brand-guide": "notifications.types.brandGuide",
  graphics: "notifications.types.graphics",
  webinar: "notifications.types.webinar",
  academy: "notifications.types.academy",
};

const timeAgo = (
  iso: string,
  t: (key: string, vars?: Record<string, string | number>) => string,
  locale: string
): string => {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return t("common.justNow");
  if (mins < 60) return t("notifications.timeAgo.minutes", { count: mins });
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return t("notifications.timeAgo.hours", { count: hrs });
  const days = Math.floor(hrs / 24);
  if (days < 7) return t("notifications.timeAgo.days", { count: days });
  return new Date(iso).toLocaleDateString(locale, {
    month: "short",
    day: "numeric",
  });
};

const NotificationBell: React.FC = () => {
  const navigate = useNavigate();
  const { t, code } = useI18n();
  const { items, newCount, markSeen } = useWhatsNewContext();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  // Opening the dropdown is the explicit acknowledgement — mark the feed seen
  // so the badge clears (here and on the dashboard) and the server baseline
  // advances for the next session.
  const toggle = () => {
    setOpen((o) => {
      const next = !o;
      if (next && newCount > 0) void markSeen();
      return next;
    });
  };

  const go = (item: WhatsNewItem) => {
    setOpen(false);
    navigate(item.route);
  };

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        aria-label={t("notifications.ariaLabel")}
        onClick={toggle}
        className="relative flex h-9 w-9 items-center justify-center rounded-lg text-white/80 hover:bg-white/10 hover:text-white transition-colors"
      >
        <i className="fa-solid fa-bell text-lg"></i>
        {newCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-emerald-500 px-1 text-[10px] font-bold text-white">
            {newCount > 9 ? t("common.countOverflow", { count: 9 }) : newCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute left-0 top-11 z-50 w-80 rounded-xl border border-gray-200 bg-white shadow-lg">
          <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
            <span className="text-sm font-semibold text-gray-900">
              {t("notifications.whatsNew.title")}
            </span>
            {newCount > 0 && (
              <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700">
                {t("notifications.whatsNew.newCount", { count: newCount })}
              </span>
            )}
          </div>

          <div className="max-h-96 overflow-y-auto">
            {items.length === 0 ? (
              <div className="px-4 py-8 text-center text-sm text-gray-400">
                {t("notifications.whatsNew.empty")}
              </div>
            ) : (
              items.map((item) => (
                <button
                  key={`${item.type}-${item.id}`}
                  onClick={() => go(item)}
                  className="flex w-full items-start gap-3 border-b border-gray-50 px-4 py-3 text-left hover:bg-gray-50"
                >
                  <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-gray-100 text-gray-600">
                    <i className={TYPE_ICON[item.type]}></i>
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center gap-2">
                      <span className="truncate text-sm font-medium text-gray-900">
                        {item.title}
                      </span>
                      {item.isNew && (
                        <span className="shrink-0 rounded-full bg-emerald-500 px-1.5 py-0.5 text-[10px] font-semibold text-white">
                          {t("notifications.whatsNew.badgeNew")}
                        </span>
                      )}
                    </span>
                    <span className="mt-0.5 flex items-center gap-2 text-xs text-gray-400">
                      <span>{t(TYPE_LABEL[item.type])}</span>
                      <span>·</span>
                      <span>{timeAgo(item.updatedAt, t, code)}</span>
                    </span>
                  </span>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
